import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import {
  Shield,
  Users,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  UserSearch,
  CalendarDays,
  HandHeart,
  Heart,
  Building2,
  Landmark,
  MessageCircle,
  Images,
  ChevronDown,
  Globe,
  Menu,
  X,
  Bell,
  BarChart3,
  Settings,
  Upload,
  Gavel,
  Share2,
  MessageSquare,
  Gift,
  Cake,
  BedDouble,
  UtensilsCrossed,
  Package,
  Boxes,
  Phone,
  Star,
  BookOpen,
  Award,
  PieChart,
  CreditCard,
  FileText,
  Megaphone,
  Camera,
  Clock,
  Bot,
  Brain,
  Search,
  Wand2,
  TrendingUp,
  Zap,
  LogIn,
} from "lucide-react";
import { Button } from "@cm/components/ui/button";
import logo from "@cm/assets/logo-optimized.png";
import { translations, type Language } from "@cm/lib/translations";

// ─── Animation variants ───
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      delay: i * 0.1,
      ease: [0.22, 1, 0.36, 1] as const,
    },
  }),
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, ease: "easeOut" as const },
  },
};

// ─── Section wrapper with scroll reveal ───
const Section = ({
  children,
  className = "",
  id,
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.section
      ref={ref}
      id={id}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={stagger}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// ─── Navbar ───
const Navbar = ({
  t,
  lang,
  setLang,
}: {
  t: Record<string, string>;
  lang: Language;
  setLang: (l: Language) => void;
}) => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const langLabels: Record<Language, string> = {
    en: "EN",
    hi: "हिं",
    gu: "ગુ",
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed left-0 right-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "border-b border-border/50 bg-card/95 shadow-lg backdrop-blur-xl"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between sm:h-20">
          {/* Logo */}
          <motion.div
            className="flex cursor-pointer items-center gap-2.5"
            whileHover={{ scale: 1.02 }}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            <img
              src={logo}
              alt="Community Management"
              className="h-10 w-auto object-contain sm:h-12"
            />
            <span className="hidden text-base font-bold leading-tight tracking-tight text-foreground sm:inline">
              Community
              <br />
              <span className="font-extrabold text-primary">Management</span>
            </span>
          </motion.div>

          {/* Desktop nav links */}
          <div className="hidden items-center gap-8 lg:flex">
            {[
              { label: t.navFeatures, href: "#features" },
              { label: t.navHowItWorks, href: "#how-it-works" },
              { label: t.navCommunity, href: "#community" },
              { label: "Contact Us", href: "/contact" },
            ].map(link => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Desktop right side */}
          <div className="hidden items-center gap-3 lg:flex">
            {/* Language switcher */}
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5 text-muted-foreground"
                onClick={() => setLangOpen(!langOpen)}
              >
                <Globe className="h-4 w-4" />
                {langLabels[lang]}
                <ChevronDown className="h-3 w-3" />
              </Button>
              {langOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute right-0 top-full mt-1 min-w-[120px] overflow-hidden rounded-xl border border-border bg-card shadow-xl"
                >
                  {(["en", "hi", "gu"] as Language[]).map(l => (
                    <button
                      key={l}
                      onClick={() => {
                        setLang(l);
                        setLangOpen(false);
                      }}
                      className={`w-full px-4 py-2.5 text-left text-sm transition-colors hover:bg-muted ${lang === l ? "bg-primary/10 font-semibold text-primary" : "text-foreground"}`}
                    >
                      {l === "en"
                        ? "English"
                        : l === "hi"
                          ? "हिन्दी"
                          : "ગુજરાતી"}
                    </button>
                  ))}
                </motion.div>
              )}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/admin")}
              className="border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground"
            >
              <Shield className="mr-1.5 h-4 w-4" /> {t.navAdminPortal}
            </Button>
            <Button
              size="sm"
              onClick={() => navigate("/member")}
              className="bg-primary text-primary-foreground shadow-lg shadow-primary/25"
            >
              <Users className="mr-1.5 h-4 w-4" /> {t.navMemberPortal}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/member")}
              className="text-muted-foreground hover:text-foreground"
            >
              <LogIn className="mr-1.5 h-4 w-4" /> Open Portal
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center gap-2 lg:hidden">
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9"
                onClick={() => setLangOpen(!langOpen)}
              >
                <Globe className="h-4 w-4" />
              </Button>
              {langOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute right-0 top-full z-50 mt-1 min-w-[120px] overflow-hidden rounded-xl border border-border bg-card shadow-xl"
                >
                  {(["en", "hi", "gu"] as Language[]).map(l => (
                    <button
                      key={l}
                      onClick={() => {
                        setLang(l);
                        setLangOpen(false);
                      }}
                      className={`w-full px-4 py-2.5 text-left text-sm hover:bg-muted ${lang === l ? "bg-primary/10 font-semibold text-primary" : "text-foreground"}`}
                    >
                      {l === "en"
                        ? "English"
                        : l === "hi"
                          ? "हिन्दी"
                          : "ગુજરાતી"}
                    </button>
                  ))}
                </motion.div>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="bg-card/98 border-t border-border backdrop-blur-xl lg:hidden"
        >
          <div className="flex flex-col gap-3 px-4 py-4">
            {[
              { label: t.navFeatures, href: "#features" },
              { label: t.navHowItWorks, href: "#how-it-works" },
              { label: t.navCommunity, href: "#community" },
              { label: "Contact Us", href: "/contact" },
            ].map(link => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className="py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
              >
                {link.label}
              </a>
            ))}
            <div className="flex flex-col gap-2 border-t border-border pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  navigate("/admin");
                  setMobileOpen(false);
                }}
                className="justify-start border-primary/30 text-primary"
              >
                <Shield className="mr-1.5 h-4 w-4" /> {t.navAdminPortal}
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  navigate("/member");
                  setMobileOpen(false);
                }}
                className="justify-start bg-primary text-primary-foreground"
              >
                <Users className="mr-1.5 h-4 w-4" /> {t.navMemberPortal}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  navigate("/member");
                  setMobileOpen(false);
                }}
                className="justify-start text-muted-foreground"
              >
                <LogIn className="mr-1.5 h-4 w-4" /> Open Portal
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </motion.nav>
  );
};

// ─── Hero Stats Card (floating) ───
const FloatingStatsCard = ({ t }: { t: Record<string, string> }) => (
  <motion.div
    initial={{ opacity: 0, x: 60, y: 20 }}
    animate={{ opacity: 1, x: 0, y: 0 }}
    transition={{ duration: 0.8, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
    className="relative hidden lg:block"
  >
    <div className="relative w-[380px]">
      {/* Verified badge */}
      <motion.div
        className="absolute -left-6 -top-4 z-20 flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 shadow-xl"
        animate={{ y: [0, -6, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      >
        <CheckCircle2 className="h-4 w-4 text-success" />
        <div>
          <p className="text-xs font-bold text-foreground">{t.heroVerified}</p>
          <p className="text-[10px] text-muted-foreground">
            {t.heroGovPartner}
          </p>
        </div>
      </motion.div>

      {/* Main stats */}
      <div className="rounded-2xl border border-border/50 bg-card/90 p-6 shadow-2xl backdrop-blur-sm">
        <div className="mb-5 grid grid-cols-3 gap-4">
          {[
            { val: "10K+", label: t.statMembers },
            { val: "50+", label: t.statCommunities },
            { val: "500+", label: t.statEvents },
          ].map((s, i) => (
            <motion.div
              key={i}
              className="rounded-xl bg-muted/50 p-3 text-center"
              whileHover={{ scale: 1.05 }}
            >
              <p className="text-xl font-extrabold text-foreground">{s.val}</p>
              <p className="mt-0.5 text-[10px] font-medium text-muted-foreground">
                {s.label}
              </p>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-3">
          {[
            {
              icon: Users,
              label: t.heroNetwork,
              color: "text-primary bg-primary/10",
            },
            {
              icon: Building2,
              label: t.heroCollaborate,
              color: "text-accent bg-accent/10",
            },
            {
              icon: Sparkles,
              label: t.heroAchieve,
              color: "text-success bg-success/10",
            },
          ].map(({ icon: Icon, label, color }, i) => (
            <motion.div
              key={i}
              className="flex flex-col items-center gap-2 rounded-xl border border-border/30 bg-muted/30 p-3"
              whileHover={{ y: -3 }}
            >
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-lg ${color}`}
              >
                <Icon className="h-5 w-5" />
              </div>
              <p className="text-[11px] font-medium text-muted-foreground">
                {label}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Bottom avatars */}
        <div className="mt-5 flex items-center justify-between border-t border-border/50 pt-4">
          <div className="flex -space-x-2">
            {["JK", "SM", "AR"].map((initials, i) => (
              <div
                key={i}
                className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-card bg-primary/20 text-[10px] font-bold text-primary"
              >
                {initials}
              </div>
            ))}
          </div>
          <div className="text-right">
            <p className="text-sm font-bold text-primary">+5.2k</p>
            <p className="text-[10px] text-muted-foreground">
              {t.heroThisMonth}
            </p>
          </div>
        </div>
      </div>
    </div>
  </motion.div>
);

// ─── Feature Card ───
const FeatureCard = ({
  icon: Icon,
  title,
  desc,
  i,
}: {
  icon: typeof Shield;
  title: string;
  desc: string;
  i: number;
}) => (
  <motion.div
    variants={fadeUp}
    custom={i}
    whileHover={{
      y: -6,
      boxShadow: "0 20px 40px -12px hsl(var(--primary) / 0.12)",
    }}
    className="group relative rounded-2xl border border-border/50 bg-card p-6 transition-all duration-300 hover:border-primary/30 sm:p-7"
  >
    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 transition-colors group-hover:bg-primary/20">
      <Icon className="h-6 w-6 text-primary" />
    </div>
    <h3 className="mb-2 text-base font-bold text-foreground">{title}</h3>
    <p className="text-sm leading-relaxed text-muted-foreground">{desc}</p>
  </motion.div>
);

// ─── Step Card ───
const StepCard = ({
  num,
  title,
  desc,
  i,
}: {
  num: string;
  title: string;
  desc: string;
  i: number;
}) => (
  <motion.div variants={fadeUp} custom={i} className="relative flex gap-5">
    <div className="flex flex-col items-center">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-lg font-extrabold text-primary-foreground shadow-lg shadow-primary/25">
        {num}
      </div>
      {i < 3 && (
        <div className="mt-2 w-0.5 flex-1 bg-gradient-to-b from-primary/40 to-transparent" />
      )}
    </div>
    <div className="pb-10">
      <h3 className="mb-1.5 text-lg font-bold text-foreground">{title}</h3>
      <p className="text-sm leading-relaxed text-muted-foreground">{desc}</p>
    </div>
  </motion.div>
);

// ─── Community Card ───
const CommunityCard = ({
  title,
  role,
  desc,
  features,
  btnText,
  onClick,
  gradient,
  icon: Icon,
  i,
}: {
  title: string;
  role: string;
  desc: string;
  features: string[];
  btnText: string;
  onClick: () => void;
  gradient: string;
  icon: React.ElementType;
  i: number;
}) => (
  <motion.div
    variants={fadeUp}
    custom={i}
    whileHover={{ y: -8 }}
    className="group relative overflow-hidden rounded-2xl border border-border/50 bg-card shadow-sm transition-all duration-300 hover:border-primary/30 hover:shadow-lg"
  >
    <div className="p-6 sm:p-8">
      {/* Icon */}
      <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
        <Icon className="h-6 w-6 text-primary" />
      </div>
      <h3 className="mb-1 text-xl font-bold text-foreground">{title}</h3>
      <p className="mb-3 text-sm font-semibold text-primary">{role}</p>
      <p className="mb-5 text-sm leading-relaxed text-muted-foreground">
        {desc}
      </p>
      <ul className="mb-6 space-y-2.5">
        {features.map((f, j) => (
          <li
            key={j}
            className="flex items-center gap-2.5 text-sm text-foreground"
          >
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
            {f}
          </li>
        ))}
      </ul>
      <button
        onClick={onClick}
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
      >
        {btnText} <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  </motion.div>
);

// ─── Main Page ───
const Index = () => {
  const navigate = useNavigate();
  const [lang, setLang] = useState<Language>("en");
  const t = translations[lang];
  const { scrollYProgress } = useScroll();
  const progressWidth = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-background text-foreground">
      {/* Global decorative background elements - matching reference site */}
      <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
        {/* Left side organic leaf/curve decorations */}
        <svg
          className="absolute -left-20 top-[10%] h-[600px] w-[400px] opacity-[0.04]"
          viewBox="0 0 400 600"
          fill="none"
        >
          <path
            d="M200 0C200 0 350 150 300 300C250 450 100 500 50 600"
            stroke="hsl(var(--primary))"
            strokeWidth="80"
            strokeLinecap="round"
          />
          <path
            d="M150 50C150 50 280 180 240 320C200 460 60 490 20 580"
            stroke="hsl(var(--primary))"
            strokeWidth="40"
            strokeLinecap="round"
          />
        </svg>
        <svg
          className="absolute -left-16 top-[45%] h-[500px] w-[350px] opacity-[0.03]"
          viewBox="0 0 350 500"
          fill="none"
        >
          <path
            d="M175 0C175 0 320 120 270 270C220 420 80 460 40 500"
            stroke="hsl(var(--primary))"
            strokeWidth="60"
            strokeLinecap="round"
          />
        </svg>
        <svg
          className="absolute -left-12 top-[75%] h-[400px] w-[300px] opacity-[0.035]"
          viewBox="0 0 300 400"
          fill="none"
        >
          <path
            d="M150 0C150 0 280 100 230 230C180 360 60 380 30 400"
            stroke="hsl(var(--accent))"
            strokeWidth="50"
            strokeLinecap="round"
          />
        </svg>
        {/* Right side subtle gradient orbs */}
        <div className="absolute -right-32 top-[20%] h-[500px] w-[500px] rounded-full bg-primary/[0.03] blur-[100px]" />
        <div className="absolute -right-20 top-[60%] h-[400px] w-[400px] rounded-full bg-accent/[0.03] blur-[80px]" />
      </div>

      {/* Scroll progress bar */}
      <motion.div
        className="fixed left-0 right-0 top-0 z-[60] h-0.5 origin-left bg-primary"
        style={{ width: progressWidth }}
      />

      <Navbar t={t} lang={lang} setLang={setLang} />

      {/* ─── HERO ─── */}
      <section className="relative overflow-hidden pb-10 pt-20 sm:pb-16 sm:pt-32">
        {/* Background gradient blobs */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <motion.div
            className="absolute -right-40 -top-40 h-[600px] w-[600px] rounded-full bg-primary/[0.04] blur-3xl"
            animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 8, repeat: Infinity }}
          />
          <motion.div
            className="absolute -bottom-40 -left-40 h-[500px] w-[500px] rounded-full bg-accent/[0.04] blur-3xl"
            animate={{ scale: [1, 1.15, 1], opacity: [0.2, 0.4, 0.2] }}
            transition={{ duration: 10, repeat: Infinity, delay: 2 }}
          />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center gap-12 lg:flex-row lg:gap-16">
            {/* Left content */}
            <div className="max-w-2xl flex-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mb-6 inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5"
              >
                <span className="h-2 w-2 animate-pulse rounded-full bg-success" />
                <span className="text-xs font-medium text-primary sm:text-sm">
                  {t.heroBadge}
                </span>
                <Sparkles className="h-3.5 w-3.5 text-primary/60" />
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.15 }}
                className="mb-4 text-2xl font-extrabold leading-[1.1] tracking-tight sm:mb-6 sm:text-5xl lg:text-6xl"
              >
                {t.heroTitle1}{" "}
                <span className="bg-gradient-to-r from-primary via-info to-primary bg-clip-text text-transparent">
                  {t.heroTitle2}
                </span>{" "}
                {t.heroTitle3}
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="mb-6 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg"
              >
                {t.heroSubtitle}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="mb-8 flex flex-wrap gap-4 sm:gap-6"
              >
                {[t.heroBullet1, t.heroBullet2, t.heroBullet3].map(
                  (text, i) => (
                    <span
                      key={i}
                      className="flex items-center gap-1.5 text-sm text-muted-foreground"
                    >
                      <CheckCircle2 className="h-4 w-4 text-success" /> {text}
                    </span>
                  )
                )}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="flex flex-wrap gap-3"
              >
                <Button
                  size="lg"
                  onClick={() => navigate("/member")}
                  className="bg-primary px-8 text-primary-foreground shadow-xl shadow-primary/25 transition-all hover:shadow-2xl hover:shadow-primary/30"
                >
                  {t.heroGetStarted} <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </motion.div>
            </div>

            {/* Right floating card */}
            <FloatingStatsCard t={t} />
          </div>
        </div>
      </section>

      {/* ─── TRUST STRIP ─── */}
      <Section className="border-b border-border/30 py-8 sm:py-10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.p
            variants={fadeUp}
            custom={0}
            className="mb-8 text-center text-xs font-semibold uppercase tracking-widest text-muted-foreground"
          >
            {t.trustTitle || "Trusted by Leading Communities"}
          </motion.p>
          <motion.div
            variants={stagger}
            className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4"
          >
            {[
              {
                val: t.trustStat1Val || "50+",
                label: t.trustStat1Label || "Communities Onboarded",
                icon: Building2,
              },
              {
                val: t.trustStat2Val || "10,000+",
                label: t.trustStat2Label || "Members Managed",
                icon: Users,
              },
              {
                val: t.trustStat3Val || "₹2Cr+",
                label: t.trustStat3Label || "Donations Processed",
                icon: Heart,
              },
              {
                val: t.trustStat4Val || "99.9%",
                label: t.trustStat4Label || "Uptime Guaranteed",
                icon: Shield,
              },
            ].map(({ val, label, icon: TIcon }, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                custom={i}
                className="flex flex-col items-center gap-2 rounded-2xl border border-border/40 bg-card p-5 text-center"
                whileHover={{
                  y: -4,
                  boxShadow: "0 12px 24px -8px hsl(var(--primary) / 0.08)",
                }}
              >
                <div className="mb-1 flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                  <TIcon className="h-5 w-5 text-primary" />
                </div>
                <p className="text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl">
                  {val}
                </p>
                <p className="text-xs font-medium text-muted-foreground sm:text-sm">
                  {label}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </Section>

      {/* ─── FEATURES ─── */}
      <Section id="features" className="bg-muted/30 py-12 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-8 text-center sm:mb-12">
            <motion.span
              variants={fadeUp}
              custom={0}
              className="mb-4 inline-block rounded-full bg-primary/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary"
            >
              {t.featuresTag}
            </motion.span>
            <motion.h2
              variants={fadeUp}
              custom={1}
              className="mb-4 text-2xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl"
            >
              {t.featuresTitle1}{" "}
              <span className="bg-gradient-to-r from-primary to-info bg-clip-text text-transparent">
                {t.featuresTitle2}
              </span>
            </motion.h2>
            <motion.p
              variants={fadeUp}
              custom={2}
              className="mx-auto max-w-2xl text-base text-muted-foreground sm:text-lg"
            >
              {t.featuresSubtitle}
            </motion.p>
          </div>

          {/* Feature Categories */}
          {[
            {
              category: t.featCat1 || "Member & Community",
              gradient: "from-primary to-info",
              iconBg: "bg-primary/10",
              iconColor: "text-primary",
              items: [
                {
                  icon: Users,
                  title: t.feat1Title,
                  anim: { scale: [1, 1.15, 1], duration: 2.5 },
                },
                {
                  icon: UserSearch,
                  title: t.feat_directory || "Member Directory",
                  anim: { x: [0, 3, -3, 0], duration: 2 },
                },
                {
                  icon: CreditCard,
                  title: t.feat_membership || "Membership Management",
                  anim: { rotateY: [0, 15, -15, 0], duration: 3 },
                },
                {
                  icon: Cake,
                  title: t.feat_birthday || "Birthday & Anniversary",
                  anim: { y: [0, -5, 0], rotate: [0, 5, -5, 0], duration: 2.5 },
                },
                {
                  icon: BookOpen,
                  title: t.feat_sadharmik || "Sadharmik Data",
                  anim: { rotateZ: [0, 3, -3, 0], duration: 3.5 },
                },
                {
                  icon: Share2,
                  title: t.feat_refer || "Refer & Earn",
                  anim: { rotate: [0, 360], duration: 8 },
                },
              ],
            },
            {
              category: t.featCat2 || "Events & Engagement",
              gradient: "from-accent to-warning",
              iconBg: "bg-accent/10",
              iconColor: "text-accent",
              items: [
                {
                  icon: CalendarDays,
                  title: t.feat2Title,
                  anim: { y: [0, -4, 0], duration: 2 },
                },
                {
                  icon: HandHeart,
                  title: t.feat3Title,
                  anim: { scale: [1, 1.2, 1], y: [0, -3, 0], duration: 2 },
                },
                {
                  icon: Heart,
                  title: t.feat4Title,
                  anim: { scale: [1, 1.25, 1, 1.25, 1], duration: 1.5 },
                },
                {
                  icon: Star,
                  title: t.feat_sponsorship || "Sponsorships",
                  anim: {
                    rotate: [0, 15, -15, 0],
                    scale: [1, 1.1, 1],
                    duration: 3,
                  },
                },
                {
                  icon: Gavel,
                  title: t.feat_auction || "Online Auction",
                  anim: { rotate: [0, -20, 0], duration: 1.5 },
                },
                {
                  icon: Camera,
                  title: t.feat_gallery || "Gallery & Media",
                  anim: { scale: [1, 0.9, 1.1, 1], duration: 2 },
                },
              ],
            },
            {
              category: t.featCat3 || "Facilities & Services",
              gradient: "from-success to-primary",
              iconBg: "bg-success/10",
              iconColor: "text-success",
              items: [
                {
                  icon: Building2,
                  title: t.feat5Title,
                  anim: { y: [0, -3, 0], duration: 3 },
                },
                {
                  icon: UtensilsCrossed,
                  title: t.feat_bhojanshala || "Bhojanshala Management",
                  anim: { rotate: [0, 10, -10, 0], duration: 2.5 },
                },
                {
                  icon: Landmark,
                  title: t.feat6Title,
                  anim: { scale: [1, 1.08, 1], y: [0, -2, 0], duration: 3.5 },
                },
                {
                  icon: BedDouble,
                  title: t.feat_hostel || "Hostel Management",
                  anim: { x: [0, 2, -2, 0], duration: 3 },
                },
                {
                  icon: Package,
                  title: t.feat_asset || "Asset Management",
                  anim: { y: [0, -4, 0], rotate: [0, 5, 0], duration: 2.5 },
                },
                {
                  icon: Boxes,
                  title: t.feat_stock || "Stock Management",
                  anim: { scale: [1, 1.1, 0.95, 1], duration: 2.5 },
                },
              ],
            },
            {
              category: t.featCat4 || "Communication & Analytics",
              gradient: "from-info to-primary",
              iconBg: "bg-info/10",
              iconColor: "text-info",
              items: [
                {
                  icon: MessageCircle,
                  title: t.feat7Title,
                  anim: { y: [0, -3, 0], scale: [1, 1.1, 1], duration: 2 },
                },
                {
                  icon: Megaphone,
                  title: t.feat_announcements || "Feed & Announcements",
                  anim: { rotate: [0, -12, 0], x: [0, 3, 0], duration: 2 },
                },
                {
                  icon: Bell,
                  title: t.feat_notifications || "Notifications",
                  anim: { rotate: [0, 15, -15, 15, 0], duration: 1.5 },
                },
                {
                  icon: Phone,
                  title: t.feat_whatsapp || "WhatsApp Integration",
                  anim: { rotate: [0, -10, 10, -10, 0], duration: 1.8 },
                },
                {
                  icon: BarChart3,
                  title: t.feat_reports || "Reports & Analytics",
                  anim: { y: [0, -5, 0], duration: 2.5 },
                },
                {
                  icon: MessageSquare,
                  title: t.feat_feedback || "Feedback Management",
                  anim: { x: [0, 4, -4, 0], duration: 2.5 },
                },
              ],
            },
          ].map((cat, catIndex) => (
            <motion.div
              key={catIndex}
              variants={fadeUp}
              custom={catIndex}
              className="mb-8 last:mb-0"
            >
              {/* Category header */}
              <div className="mb-5 flex items-center gap-3">
                <div
                  className={`h-1.5 w-10 rounded-full bg-gradient-to-r ${cat.gradient}`}
                />
                <h3 className="text-lg font-bold text-foreground">
                  {cat.category}
                </h3>
                <div className="h-px flex-1 bg-border/50" />
              </div>

              {/* Category items */}
              <motion.div
                variants={stagger}
                className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-6"
              >
                {cat.items.map(({ icon: Icon, title, anim }, i) => (
                  <motion.div
                    key={i}
                    variants={fadeUp}
                    custom={i}
                    whileHover={{
                      y: -6,
                      scale: 1.04,
                      boxShadow: "0 16px 32px -8px hsl(var(--primary) / 0.1)",
                    }}
                    className="group relative flex cursor-pointer flex-col items-center gap-3 overflow-hidden rounded-2xl border border-border/50 bg-card p-5 transition-all duration-300 hover:border-primary/30"
                  >
                    <motion.div
                      className={`relative z-10 h-12 w-12 rounded-xl ${cat.iconBg} flex items-center justify-center`}
                      animate={{ ...anim, duration: undefined }}
                      transition={{
                        duration: (anim as any).duration || 3,
                        repeat: Infinity,
                        ease: "easeInOut" as const,
                        repeatType: "reverse" as const,
                      }}
                    >
                      <Icon className={`h-6 w-6 ${cat.iconColor}`} />
                    </motion.div>
                    <p className="relative z-10 text-center text-xs font-semibold leading-tight text-foreground sm:text-sm">
                      {title}
                    </p>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          ))}

          {/* Total modules badge */}
          <motion.div
            variants={fadeUp}
            custom={5}
            className="mt-8 flex justify-center"
          >
            <div className="inline-flex flex-col items-center gap-1 rounded-full bg-primary/10 px-5 py-2 text-center sm:flex-row sm:gap-2 sm:px-6 sm:py-2.5">
              <div className="flex items-center gap-1.5">
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="text-sm font-bold text-primary">
                  {t.feat_total || "24+ Powerful Modules"}
                </span>
              </div>
              <span className="text-[11px] text-muted-foreground sm:text-xs">
                {t.feat_total_desc ||
                  "Everything you need to manage your community"}
              </span>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* ─── AI FEATURES ─── */}
      <Section className="relative overflow-hidden py-12 sm:py-24">
        {/* Background effects */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute left-1/2 top-0 h-[800px] w-[800px] -translate-x-1/2 rounded-full bg-gradient-to-br from-primary/5 via-accent/5 to-info/5 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center sm:mb-16">
            <motion.div
              variants={fadeUp}
              custom={0}
              className="mb-5 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-primary/10 to-accent/10 px-5 py-2"
            >
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-xs font-bold uppercase tracking-wider text-primary">
                AI-Powered Platform
              </span>
              <Zap className="h-3.5 w-3.5 text-accent" />
            </motion.div>
            <motion.h2
              variants={fadeUp}
              custom={1}
              className="mb-4 text-2xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl"
            >
              Supercharged with{" "}
              <span className="bg-gradient-to-r from-primary via-accent to-info bg-clip-text text-transparent">
                Artificial Intelligence
              </span>
            </motion.h2>
            <motion.p
              variants={fadeUp}
              custom={2}
              className="mx-auto max-w-2xl text-base text-muted-foreground sm:text-lg"
            >
              Every module is enhanced with AI capabilities — from smart
              chatbots to predictive analytics, making your community management
              effortless and intelligent.
            </motion.p>
          </div>

          {/* AI Feature Cards */}
          <motion.div
            variants={stagger}
            className="mb-12 grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4"
          >
            {[
              {
                icon: Bot,
                title: "AI Chatbot Assistant",
                desc: "24/7 intelligent assistant for admins & members. Ask anything about events, donations, bookings, or community data — get instant, context-aware answers.",
                features: [
                  "Natural language queries",
                  "Context-aware responses",
                  "Multi-language support",
                ],
                gradient: "from-primary to-info",
                iconBg: "bg-primary/10",
                delay: 0,
              },
              {
                icon: Wand2,
                title: "AI Content Generator",
                desc: "Auto-generate event descriptions, announcements, donation appeals, WhatsApp messages, and newsletter content with one click.",
                features: [
                  "Event invitations",
                  "Donation appeals",
                  "WhatsApp broadcasts",
                ],
                gradient: "from-accent to-warning",
                iconBg: "bg-accent/10",
                delay: 0.1,
              },
              {
                icon: Search,
                title: "AI Smart Search",
                desc: "Natural language search across all modules. Just describe what you need — the AI understands intent and finds relevant data instantly.",
                features: [
                  "Natural language input",
                  "Cross-module search",
                  "Intelligent suggestions",
                ],
                gradient: "from-success to-primary",
                iconBg: "bg-success/10",
                delay: 0.2,
              },
              {
                icon: Brain,
                title: "AI Analytics & Insights",
                desc: "Get predictive analytics, trend analysis, and actionable recommendations for membership growth, donation patterns, and event engagement.",
                features: [
                  "Trend predictions",
                  "Growth analysis",
                  "Smart recommendations",
                ],
                gradient: "from-info to-primary",
                iconBg: "bg-info/10",
                delay: 0.3,
              },
            ].map((feature, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                custom={i}
                whileHover={{
                  y: -8,
                  boxShadow: "0 24px 48px -12px hsl(var(--primary) / 0.15)",
                }}
                className="group relative overflow-hidden rounded-2xl border border-border/50 bg-card transition-all duration-300 hover:border-primary/30"
              >
                {/* Top gradient bar */}
                <div className={`h-1.5 bg-gradient-to-r ${feature.gradient}`} />

                <div className="p-6">
                  {/* Icon */}
                  <motion.div
                    className={`h-14 w-14 rounded-2xl ${feature.iconBg} mb-4 flex items-center justify-center`}
                    animate={{ scale: [1, 1.08, 1] }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  >
                    <feature.icon className="h-7 w-7 text-primary" />
                  </motion.div>

                  <h3 className="mb-2 text-lg font-bold text-foreground">
                    {feature.title}
                  </h3>
                  <p className="mb-4 text-sm leading-relaxed text-muted-foreground">
                    {feature.desc}
                  </p>

                  {/* Feature pills */}
                  <div className="flex flex-wrap gap-1.5">
                    {feature.features.map((f, j) => (
                      <span
                        key={j}
                        className="bg-primary/8 inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-medium text-primary"
                      >
                        <CheckCircle2 className="h-2.5 w-2.5" />
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* AI Stats Bar */}
          <motion.div
            variants={fadeUp}
            custom={5}
            className="rounded-2xl border border-border/50 bg-gradient-to-r from-primary/5 via-accent/5 to-info/5 p-6 sm:p-8"
          >
            <div className="grid grid-cols-2 gap-6 sm:gap-8 lg:grid-cols-4">
              {[
                {
                  icon: Bot,
                  stat: "24/7",
                  label: "AI Assistant Available",
                  color: "text-primary",
                },
                {
                  icon: Wand2,
                  stat: "10+",
                  label: "Content Templates",
                  color: "text-accent",
                },
                {
                  icon: TrendingUp,
                  stat: "Real-time",
                  label: "Predictive Analytics",
                  color: "text-success",
                },
                {
                  icon: Zap,
                  stat: "Instant",
                  label: "AI-Powered Responses",
                  color: "text-info",
                },
              ].map(({ icon: Icon, stat, label, color }, i) => (
                <motion.div
                  key={i}
                  className="flex items-center gap-3 sm:gap-4"
                  whileHover={{ scale: 1.03 }}
                >
                  <div
                    className={`h-11 w-11 rounded-xl ${color === "text-primary" ? "bg-primary/10" : color === "text-accent" ? "bg-accent/10" : color === "text-success" ? "bg-success/10" : "bg-info/10"} flex shrink-0 items-center justify-center`}
                  >
                    <Icon className={`h-5 w-5 ${color}`} />
                  </div>
                  <div>
                    <p className={`text-xl font-extrabold ${color}`}>{stat}</p>
                    <p className="text-xs font-medium text-muted-foreground">
                      {label}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* AI CTA */}
          <motion.div
            variants={fadeUp}
            custom={6}
            className="mt-10 text-center"
          >
            <p className="mb-4 text-sm text-muted-foreground">
              AI features are available across{" "}
              <strong className="text-foreground">all 24+ modules</strong> in
              both Admin & Member portals
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button
                size="lg"
                className="gap-2 shadow-lg shadow-primary/20"
                onClick={() => navigate("/admin")}
              >
                <Sparkles className="h-4 w-4" /> Try Admin AI Features
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="gap-2 border-primary/30"
                onClick={() => navigate("/member")}
              >
                <Bot className="h-4 w-4" /> Explore Member AI
              </Button>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* ─── HOW IT WORKS ─── */}
      <Section id="how-it-works" className="py-12 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-start gap-12 lg:grid-cols-2 lg:gap-20">
            <div>
              <motion.span
                variants={fadeUp}
                custom={0}
                className="mb-4 inline-block rounded-full bg-accent/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-accent"
              >
                {t.howTag}
              </motion.span>
              <motion.h2
                variants={fadeUp}
                custom={1}
                className="mb-4 text-2xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl"
              >
                {t.howTitle1}{" "}
                <span className="bg-gradient-to-r from-accent to-warning bg-clip-text text-transparent">
                  {t.howTitle2}
                </span>
              </motion.h2>
              <motion.p
                variants={fadeUp}
                custom={2}
                className="max-w-md text-base text-muted-foreground sm:text-lg"
              >
                {t.howSubtitle}
              </motion.p>
            </div>

            <div className="flex flex-col">
              {[
                { num: "01", title: t.how1Title, desc: t.how1Desc },
                { num: "02", title: t.how2Title, desc: t.how2Desc },
                { num: "03", title: t.how3Title, desc: t.how3Desc },
                { num: "04", title: t.how4Title, desc: t.how4Desc },
              ].map((step, i) => (
                <StepCard
                  key={i}
                  num={step.num}
                  title={step.title}
                  desc={step.desc}
                  i={i}
                />
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* ─── COMMUNITY ─── */}
      <Section id="community" className="bg-muted/30 py-12 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-8 text-center sm:mb-12">
            <motion.span
              variants={fadeUp}
              custom={0}
              className="mb-4 inline-block rounded-full bg-success/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-success"
            >
              {t.communityTag}
            </motion.span>
            <motion.h2
              variants={fadeUp}
              custom={1}
              className="mb-4 text-2xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl"
            >
              {t.communityTitle1}{" "}
              <span className="bg-gradient-to-r from-success to-primary bg-clip-text text-transparent">
                {t.communityTitle2}
              </span>
            </motion.h2>
            <motion.p
              variants={fadeUp}
              custom={2}
              className="mx-auto max-w-2xl text-base text-muted-foreground sm:text-lg"
            >
              {t.communitySubtitle}
            </motion.p>
          </div>

          <motion.div
            variants={stagger}
            className="grid gap-5 sm:gap-6 md:grid-cols-3"
          >
            <CommunityCard
              i={0}
              icon={Users}
              title={t.comm1Title}
              role={t.comm1Role}
              desc={t.comm1Desc}
              features={[t.comm1F1, t.comm1F2, t.comm1F3, t.comm1F4]}
              btnText={t.comm1Btn}
              onClick={() => navigate("/member")}
              gradient=""
            />
            <CommunityCard
              i={1}
              icon={Clock}
              title={t.comm2Title}
              role={t.comm2Role}
              desc={t.comm2Desc}
              features={[t.comm2F1, t.comm2F2, t.comm2F3, t.comm2F4]}
              btnText={t.comm2Btn}
              onClick={() => navigate("/admin")}
              gradient=""
            />
            <CommunityCard
              i={2}
              icon={FileText}
              title={t.comm3Title}
              role={t.comm3Role}
              desc={t.comm3Desc}
              features={[t.comm3F1, t.comm3F2, t.comm3F3, t.comm3F4]}
              btnText={t.comm3Btn}
              onClick={() => navigate("/member/donations")}
              gradient=""
            />
          </motion.div>
        </div>
      </Section>

      {/* ─── CTA ─── */}
      <Section className="py-12 sm:py-16">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <motion.h2
            variants={fadeUp}
            custom={0}
            className="mb-4 text-2xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl"
          >
            {t.ctaTitle}
          </motion.h2>
          <motion.p
            variants={fadeUp}
            custom={1}
            className="mx-auto mb-8 max-w-2xl text-base text-muted-foreground sm:text-lg"
          >
            {t.ctaSubtitle}
          </motion.p>

          <motion.div
            variants={stagger}
            className="mx-auto mb-8 grid max-w-lg gap-4 sm:grid-cols-2"
          >
            <motion.div
              variants={scaleIn}
              whileHover={{ scale: 1.03 }}
              onClick={() => navigate("/admin")}
              className="cursor-pointer rounded-2xl border-2 border-primary/30 bg-primary/5 p-6 transition-all hover:border-primary/60"
            >
              <Shield className="mx-auto mb-3 h-8 w-8 text-primary" />
              <h3 className="font-bold text-foreground">{t.ctaAdmin}</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {t.ctaAdminDesc}
              </p>
            </motion.div>
            <motion.div
              variants={scaleIn}
              whileHover={{ scale: 1.03 }}
              onClick={() => navigate("/member")}
              className="cursor-pointer rounded-2xl border-2 border-accent/30 bg-accent/5 p-6 transition-all hover:border-accent/60"
            >
              <Users className="mx-auto mb-3 h-8 w-8 text-accent" />
              <h3 className="font-bold text-foreground">{t.ctaMember}</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {t.ctaMemberDesc}
              </p>
            </motion.div>
          </motion.div>

          <motion.div variants={fadeUp} custom={2}>
            <Button
              size="lg"
              onClick={() => navigate("/member")}
              className="bg-primary px-10 text-primary-foreground shadow-xl shadow-primary/25"
            >
              {t.ctaGetStarted} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </motion.div>
        </div>
      </Section>

      {/* ─── FOOTER ─── */}
      <footer className="relative overflow-hidden">
        {/* Decorative gradient overlay - navy to teal */}
        <div className="absolute inset-0 bg-gradient-to-br from-[hsl(220,35%,16%)] via-[hsl(210,30%,18%)] to-[hsl(175,50%,30%)]" />
        {/* Subtle decorative dots */}
        <div className="absolute left-1/3 top-16 h-2 w-2 rounded-full bg-primary/40" />
        <div className="absolute right-1/3 top-32 h-2 w-2 rounded-full bg-pink-400/40" />
        <div className="absolute bottom-20 left-1/2 h-2 w-2 rounded-full bg-primary/30" />

        <div className="relative mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
            {/* Left Side - Contact Info */}
            <div>
              <div className="mb-2 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/20">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="hsl(180,65%,50%)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M2 12h20" />
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-white sm:text-3xl">
                  {t.footerCompany === "Company"
                    ? "Let's Build Something"
                    : t.footerCompany}{" "}
                  <span className="text-primary">
                    {t.footerCompany === "Company" ? "Amazing" : ""}
                  </span>
                </h3>
              </div>
              <p className="mb-8 ml-[52px] text-sm text-[hsl(215,10%,55%)]">
                Empowering communities with smart management solutions.
              </p>

              {/* Contact Cards */}
              <div className="mb-8 space-y-3">
                <div className="flex items-center gap-4 rounded-xl border border-[hsl(215,20%,22%)] bg-[hsl(215,25%,16%)] px-5 py-4 transition-colors hover:border-primary/30">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-orange-500/20">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#f97316"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  </div>
                  <span className="text-xs leading-relaxed text-[hsl(215,10%,75%)] sm:text-sm">
                    4, Ramya, Polytechnic Road, Opp Ketav Petrol Pump, Ambawadi,
                    Ahmedabad, Gujarat – 380015
                  </span>
                </div>
                <div className="flex items-center gap-4 rounded-xl border border-[hsl(215,20%,22%)] bg-[hsl(215,25%,16%)] px-5 py-4 transition-colors hover:border-primary/30">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/20">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="hsl(180,65%,50%)"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect width="20" height="16" x="2" y="4" rx="2" />
                      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                    </svg>
                  </div>
                  <span className="text-sm text-[hsl(215,10%,75%)]">
                    contact@universal-software.com
                  </span>
                </div>
                <div className="flex items-center gap-4 rounded-xl border border-[hsl(215,20%,22%)] bg-[hsl(215,25%,16%)] px-5 py-4 transition-colors hover:border-primary/30">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-500/20">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#22c55e"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                  </div>
                  <span className="text-sm text-[hsl(215,10%,75%)]">
                    (+91) 9512001091
                  </span>
                </div>
              </div>

              {/* Social Icons */}
              <div className="flex items-center gap-3">
                <span className="mr-1 text-xs text-[hsl(215,10%,50%)]">
                  ✨ Follow
                </span>
                {[
                  {
                    href: "https://www.instagram.com/universalsoftware",
                    label: "Instagram",
                    path: "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z",
                  },
                  {
                    href: "https://www.facebook.com/universalsoftware",
                    label: "Facebook",
                    path: "M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z",
                  },
                  {
                    href: "https://in.linkedin.com/company/universal-software",
                    label: "LinkedIn",
                    path: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z",
                  },
                ].map(s => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    className="group flex h-9 w-9 items-center justify-center rounded-full border border-[hsl(215,20%,25%)] bg-[hsl(215,25%,18%)] text-[hsl(215,10%,60%)] transition-all duration-300 hover:scale-110 hover:border-primary hover:bg-primary hover:text-white"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d={s.path} />
                    </svg>
                  </a>
                ))}
              </div>
            </div>

            {/* Right Side - Send a Message Form */}
            <div>
              <div className="relative rounded-2xl bg-white p-6 shadow-xl sm:p-8">
                {/* Sparkle icon */}
                <div className="absolute right-5 top-5 text-primary/40">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
                  </svg>
                </div>
                <div className="mb-6 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="hsl(180,65%,40%)"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect width="20" height="16" x="2" y="4" rx="2" />
                      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-[hsl(215,30%,15%)]">
                      Send a Message
                    </h4>
                    <p className="text-xs text-[hsl(215,10%,55%)]">
                      We'll respond within 24h
                    </p>
                  </div>
                </div>
                <form
                  onSubmit={e => {
                    e.preventDefault();
                    toast.success("Message sent successfully!");
                  }}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 gap-4">
                    <input
                      type="text"
                      placeholder="Your name"
                      required
                      className="w-full rounded-lg border border-[hsl(215,10%,88%)] bg-white px-4 py-3 text-sm text-[hsl(215,30%,15%)] transition-all placeholder:text-[hsl(215,10%,65%)] focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                    <input
                      type="email"
                      placeholder="your@email.com"
                      required
                      className="w-full rounded-lg border border-[hsl(215,10%,88%)] bg-white px-4 py-3 text-sm text-[hsl(215,30%,15%)] transition-all placeholder:text-[hsl(215,10%,65%)] focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                  <textarea
                    placeholder="Your message..."
                    rows={3}
                    required
                    className="w-full resize-none rounded-lg border border-[hsl(215,10%,88%)] bg-white px-4 py-3 text-sm text-[hsl(215,30%,15%)] transition-all placeholder:text-[hsl(215,10%,65%)] focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                  <button
                    type="submit"
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-primary via-[hsl(200,60%,35%)] to-[hsl(215,50%,30%)] py-3.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="m22 2-7 20-4-9-9-4z" />
                      <path d="M22 2 11 13" />
                    </svg>
                    Send Message →
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="mt-12 flex flex-col items-center gap-4 border-t border-[hsl(215,20%,18%)] pt-6 text-center">
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
              <button
                type="button"
                className="cursor-pointer border-0 bg-transparent p-0 text-xs text-[hsl(215,10%,45%)] transition-colors hover:text-primary"
                onClick={() => navigate("/privacy-policy")}
              >
                {t.footerPrivacy}
              </button>
              <button
                type="button"
                className="cursor-pointer border-0 bg-transparent p-0 text-xs text-[hsl(215,10%,45%)] transition-colors hover:text-primary"
                onClick={() => navigate("/terms-of-service")}
              >
                {t.footerTerms}
              </button>
            </div>
            <p className="text-xs text-[hsl(215,10%,45%)]">
              Powered by{" "}
              <a
                href="https://www.universal-software.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-primary hover:underline"
              >
                Universal Software
              </a>
            </p>
            <p className="text-xs text-[hsl(215,10%,45%)]">{t.footerRights}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
